import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, RotateCcw, X } from 'lucide-react';

import { TearDropCornerHandle, type TearDropCorner } from './pas/TearDropCornerHandle';
import { assetService, peekSignedOssUrlCache } from '../services/assetService';
import type { ImageCropRect } from '../utils/embeddedImageMetadata';
import { buildAssetPreviewUrl } from '../utils/mediaUrl';

type ImageCropEditorModalProps = {
  open: boolean;
  imageUrl: string;
  ossId?: string | null;
  initialCropRect?: ImageCropRect | null;
  onClose: () => void;
  onApply: (result: { cropRect: ImageCropRect; imageWidth: number; imageHeight: number }) => void | Promise<void>;
  /** 无选区时点击主按钮：清除整张嵌入图（含生图槽） */
  onClearEmbed?: () => void | Promise<void>;
  /** 是否已有嵌入图（允许「清除嵌入图」） */
  allowClearEmbed?: boolean;
  applying?: boolean;
  title?: string;
};

type DisplayMetrics = {
  scale: number;
  offsetX: number;
  offsetY: number;
  displayWidth: number;
  displayHeight: number;
  naturalWidth: number;
  naturalHeight: number;
};

type DisplayRect = { x: number; y: number; width: number; height: number };

type DragState =
  | { type: 'create'; startX: number; startY: number }
  | { type: 'move'; startX: number; startY: number; origin: DisplayRect }
  | { type: 'resize'; corner: TearDropCorner; startX: number; startY: number; origin: DisplayRect };

const HANDLE_HIT = 28;
const MIN_RECT_SIZE = 18;
const CORNERS: TearDropCorner[] = ['nw', 'ne', 'sw', 'se'];

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function toHttps(url: string): string {
  return url.replace(/^http:\/\//i, 'https://');
}

function extractOssKeyFromUrl(rawUrl?: string | null): string {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.startsWith('//') ? `https:${trimmed}` : trimmed);
    const key = parsed.pathname.replace(/^\/+/, '').trim();
    if (key.startsWith('assets/') || key.startsWith('ttq/')) return key;
  } catch {
    // ignore
  }
  return '';
}

function naturalToDisplay(rect: ImageCropRect, metrics: DisplayMetrics): DisplayRect {
  return {
    x: metrics.offsetX + rect.x * metrics.scale,
    y: metrics.offsetY + rect.y * metrics.scale,
    width: rect.width * metrics.scale,
    height: rect.height * metrics.scale,
  };
}

function displayToNatural(rect: DisplayRect, metrics: DisplayMetrics): ImageCropRect {
  const x = clamp(Math.round((rect.x - metrics.offsetX) / metrics.scale), 0, metrics.naturalWidth - 1);
  const y = clamp(Math.round((rect.y - metrics.offsetY) / metrics.scale), 0, metrics.naturalHeight - 1);
  const width = clamp(Math.round(rect.width / metrics.scale), 1, metrics.naturalWidth - x);
  const height = clamp(Math.round(rect.height / metrics.scale), 1, metrics.naturalHeight - y);
  return { x, y, width, height };
}

function normalizeDragRect(startX: number, startY: number, endX: number, endY: number): DisplayRect {
  const x = Math.min(startX, endX);
  const y = Math.min(startY, endY);
  return {
    x,
    y,
    width: Math.abs(endX - startX),
    height: Math.abs(endY - startY),
  };
}

function pointInRect(x: number, y: number, rect: DisplayRect): boolean {
  return x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;
}

function findHandle(x: number, y: number, rect: DisplayRect): TearDropCorner | null {
  const left = rect.x;
  const top = rect.y;
  const right = rect.x + rect.width;
  const bottom = rect.y + rect.height;
  if (x >= left - HANDLE_HIT && x <= left && y >= top - HANDLE_HIT && y <= top) return 'nw';
  if (x >= right && x <= right + HANDLE_HIT && y >= top - HANDLE_HIT && y <= top) return 'ne';
  if (x >= left - HANDLE_HIT && x <= left && y >= bottom && y <= bottom + HANDLE_HIT) return 'sw';
  if (x >= right && x <= right + HANDLE_HIT && y >= bottom && y <= bottom + HANDLE_HIT) return 'se';
  return null;
}

function clampRectToImage(rect: DisplayRect, metrics: DisplayMetrics): DisplayRect {
  const minX = metrics.offsetX;
  const minY = metrics.offsetY;
  const maxX = metrics.offsetX + metrics.displayWidth;
  const maxY = metrics.offsetY + metrics.displayHeight;
  const width = clamp(rect.width, MIN_RECT_SIZE, metrics.displayWidth);
  const height = clamp(rect.height, MIN_RECT_SIZE, metrics.displayHeight);
  const x = clamp(rect.x, minX, maxX - width);
  const y = clamp(rect.y, minY, maxY - height);
  return { x, y, width, height };
}

function resizeRectByCorner(
  origin: DisplayRect,
  corner: TearDropCorner,
  pointerX: number,
  pointerY: number,
  metrics: DisplayMetrics,
): DisplayRect {
  const minX = metrics.offsetX;
  const minY = metrics.offsetY;
  const maxX = metrics.offsetX + metrics.displayWidth;
  const maxY = metrics.offsetY + metrics.displayHeight;
  let left = origin.x;
  let top = origin.y;
  let right = origin.x + origin.width;
  let bottom = origin.y + origin.height;

  if (corner.includes('w')) {
    left = clamp(pointerX, minX, right - MIN_RECT_SIZE);
  }
  if (corner.includes('e')) {
    right = clamp(pointerX, left + MIN_RECT_SIZE, maxX);
  }
  if (corner.includes('n')) {
    top = clamp(pointerY, minY, bottom - MIN_RECT_SIZE);
  }
  if (corner.includes('s')) {
    bottom = clamp(pointerY, top + MIN_RECT_SIZE, maxY);
  }

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}

function cornerCursor(corner: TearDropCorner): string {
  if (corner === 'nw' || corner === 'se') return 'nwse-resize';
  return 'nesw-resize';
}

export function ImageCropEditorModal({
  open,
  imageUrl,
  ossId,
  initialCropRect,
  onClose,
  onApply,
  onClearEmbed,
  allowClearEmbed = false,
  applying = false,
  title = '编辑嵌入图片',
}: ImageCropEditorModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [layoutTick, setLayoutTick] = useState(0);
  const [displayRect, setDisplayRect] = useState<DisplayRect | null>(null);
  const [hoverCursor, setHoverCursor] = useState('crosshair');
  const dragRef = useRef<DragState | null>(null);
  const seededInitialRef = useRef(false);
  const retryingRef = useRef(false);

  const resolvedOssId = useMemo(
    () => String(ossId || '').trim() || extractOssKeyFromUrl(imageUrl),
    [imageUrl, ossId],
  );

  const metrics = useMemo((): DisplayMetrics | null => {
    void layoutTick;
    const container = containerRef.current;
    const image = imageRef.current;
    if (!container || !image || !image.naturalWidth || !image.naturalHeight) return null;
    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    const displayWidth = imageRect.width;
    const displayHeight = imageRect.height;
    if (displayWidth <= 0 || displayHeight <= 0) return null;
    return {
      scale: displayWidth / image.naturalWidth,
      offsetX: imageRect.left - containerRect.left,
      offsetY: imageRect.top - containerRect.top,
      displayWidth,
      displayHeight,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
    };
  }, [layoutTick, imageLoaded, open]);

  const refreshLayout = useCallback(() => {
    setLayoutTick((value) => value + 1);
  }, []);

  useEffect(() => {
    if (!open) {
      setResolvedUrl(null);
      setResolving(false);
      setLoadError(null);
      setImageLoaded(false);
      setDisplayRect(null);
      setHoverCursor('crosshair');
      dragRef.current = null;
      seededInitialRef.current = false;
      retryingRef.current = false;
      return;
    }

    let cancelled = false;
    setLoadError(null);
    setImageLoaded(false);
    setDisplayRect(null);
    seededInitialRef.current = false;
    retryingRef.current = false;

    // 乐观展示：优先用与上方 ProxyImage 相同的签名缓存，命中浏览器图片缓存
    const quickUrl = (() => {
      if (resolvedOssId) {
        const cached = peekSignedOssUrlCache(resolvedOssId);
        if (cached) return toHttps(cached);
      }
      const fromRaw = buildAssetPreviewUrl(imageUrl);
      if (fromRaw) return toHttps(fromRaw);
      const trimmed = String(imageUrl || '').trim();
      if (/^https?:\/\//i.test(trimmed)) return toHttps(trimmed);
      return '';
    })();
    if (quickUrl) {
      setResolvedUrl(quickUrl);
      setResolving(false);
    } else {
      setResolvedUrl(null);
      setResolving(true);
    }

    void (async () => {
      if (resolvedOssId) {
        try {
          const signedUrl = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
          if (cancelled) return;
          if (signedUrl) {
            // 与乐观 URL 相同时不触发重载；不同则换签名直链（缓存命中时几乎瞬时）
            setResolvedUrl((prev) => (prev === signedUrl ? prev : signedUrl));
            setResolving(false);
            return;
          }
        } catch {
          // fall through
        }
      }

      if (cancelled) return;
      if (!quickUrl) {
        const fromRaw = buildAssetPreviewUrl(imageUrl);
        setResolvedUrl(fromRaw);
        setResolving(false);
        if (!fromRaw) setLoadError('无法解析素材原图地址');
      } else {
        setResolving(false);
      }
    })();

    const onResize = () => refreshLayout();
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
    };
  }, [open, imageUrl, resolvedOssId, refreshLayout]);

  useEffect(() => {
    if (!open || !metrics || !initialCropRect || seededInitialRef.current) return;
    setDisplayRect(clampRectToImage(naturalToDisplay(initialCropRect, metrics), metrics));
    seededInitialRef.current = true;
  }, [open, metrics, initialCropRect]);

  const handleImageError = useCallback(async () => {
    if (retryingRef.current || !resolvedOssId) {
      setLoadError('素材原图加载失败');
      setImageLoaded(false);
      return;
    }
    retryingRef.current = true;
    try {
      const signedUrl = toHttps(await assetService.getSignedOssUrl(resolvedOssId));
      if (signedUrl && signedUrl !== resolvedUrl) {
        setResolvedUrl(signedUrl);
        setImageLoaded(false);
        setLoadError(null);
        return;
      }
    } catch {
      // fall through
    }
    setLoadError('素材原图加载失败');
    setImageLoaded(false);
  }, [resolvedOssId, resolvedUrl]);

  const resolveHoverCursor = useCallback((x: number, y: number) => {
    if (dragRef.current || applying || !imageLoaded) return;
    if (displayRect && displayRect.width >= MIN_RECT_SIZE && displayRect.height >= MIN_RECT_SIZE) {
      const handle = findHandle(x, y, displayRect);
      if (handle) {
        setHoverCursor(cornerCursor(handle));
        return;
      }
      if (pointInRect(x, y, displayRect)) {
        setHoverCursor('move');
        return;
      }
    }
    setHoverCursor('crosshair');
  }, [applying, displayRect, imageLoaded]);

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!metrics || applying || !imageLoaded) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;

    if (displayRect && displayRect.width >= MIN_RECT_SIZE && displayRect.height >= MIN_RECT_SIZE) {
      const handle = findHandle(x, y, displayRect);
      if (handle) {
        dragRef.current = {
          type: 'resize',
          corner: handle,
          startX: x,
          startY: y,
          origin: { ...displayRect },
        };
        setHoverCursor(cornerCursor(handle));
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }

      if (pointInRect(x, y, displayRect)) {
        dragRef.current = {
          type: 'move',
          startX: x,
          startY: y,
          origin: { ...displayRect },
        };
        setHoverCursor('move');
        event.currentTarget.setPointerCapture(event.pointerId);
        return;
      }
    }

    dragRef.current = { type: 'create', startX: x, startY: y };
    setDisplayRect({ x, y, width: 0, height: 0 });
    setHoverCursor('crosshair');
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [applying, displayRect, imageLoaded, metrics]);

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    const drag = dragRef.current;

    if (!drag) {
      resolveHoverCursor(x, y);
      return;
    }
    if (!metrics) return;

    if (drag.type === 'create') {
      setDisplayRect(normalizeDragRect(drag.startX, drag.startY, x, y));
      return;
    }

    if (drag.type === 'move') {
      const next = clampRectToImage({
        x: drag.origin.x + (x - drag.startX),
        y: drag.origin.y + (y - drag.startY),
        width: drag.origin.width,
        height: drag.origin.height,
      }, metrics);
      setDisplayRect(next);
      return;
    }

    setDisplayRect(resizeRectByCorner(drag.origin, drag.corner, x, y, metrics));
  }, [metrics, resolveHoverCursor]);

  const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (drag?.type === 'create' && metrics) {
      setDisplayRect((current) => {
        if (!current || current.width < MIN_RECT_SIZE || current.height < MIN_RECT_SIZE) {
          return null;
        }
        return clampRectToImage(current, metrics);
      });
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    resolveHoverCursor(event.clientX - bounds.left, event.clientY - bounds.top);
  }, [metrics, resolveHoverCursor]);

  const handleApply = async () => {
    if (!metrics || !displayRect || displayRect.width < MIN_RECT_SIZE || displayRect.height < MIN_RECT_SIZE) return;
    const cropRect = displayToNatural(displayRect, metrics);
    await onApply({
      cropRect,
      imageWidth: metrics.naturalWidth,
      imageHeight: metrics.naturalHeight,
    });
  };

  if (!open) return null;

  const showSelection = Boolean(
    displayRect && displayRect.width > 0 && displayRect.height > 0,
  );
  const showHandles = Boolean(
    displayRect
    && displayRect.width >= MIN_RECT_SIZE
    && displayRect.height >= MIN_RECT_SIZE
    && dragRef.current?.type !== 'create',
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        className="flex h-[min(92vh,760px)] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-ink">{title}</h3>
            <p className="text-xs text-muted">拖拽四角水滴手柄调整选区，或在空白处重新框选</p>
          </div>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-black/[0.05]"
            aria-label="关闭"
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </div>

        <div
          ref={containerRef}
          className="relative min-h-0 flex-1 touch-none select-none bg-[#eef3f8]"
          style={{ cursor: hoverCursor }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          {resolvedUrl ? (
            <img
              ref={imageRef}
              src={resolvedUrl}
              alt="素材原图"
              className="pointer-events-none absolute inset-0 m-auto max-h-full max-w-full object-contain"
              draggable={false}
              referrerPolicy="no-referrer"
              onLoad={() => {
                setImageLoaded(true);
                setLoadError(null);
                refreshLayout();
              }}
              onError={() => { void handleImageError(); }}
            />
          ) : null}

          {metrics ? (
            <div
              className="pointer-events-none absolute border border-primary/20 bg-black/10"
              style={{
                left: metrics.offsetX,
                top: metrics.offsetY,
                width: metrics.displayWidth,
                height: metrics.displayHeight,
              }}
            />
          ) : null}

          {showSelection && displayRect ? (
            <div
              className="pointer-events-none absolute border-2 border-dashed border-[#2563eb] bg-[#2563eb]/15"
              style={{
                left: displayRect.x,
                top: displayRect.y,
                width: displayRect.width,
                height: displayRect.height,
              }}
            >
              {showHandles
                ? CORNERS.map((corner) => (
                    <TearDropCornerHandle key={corner} corner={corner} color="#2563eb" />
                  ))
                : null}
            </div>
          ) : null}

          {resolving || (!imageLoaded && !loadError) ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="animate-spin text-primary" size={24} />
            </div>
          ) : null}

          {loadError ? (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-danger">
              {loadError}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-muted hover:bg-black/[0.03]"
            disabled={applying}
            onClick={() => {
              setDisplayRect(null);
              seededInitialRef.current = true;
            }}
          >
            <RotateCcw size={14} />
            清除选区
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-lg border border-border px-4 py-2 text-sm text-muted hover:bg-black/[0.03]"
              disabled={applying}
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
              disabled={
                applying
                || (
                  displayRect
                    ? (displayRect.width < MIN_RECT_SIZE || displayRect.height < MIN_RECT_SIZE)
                    : !allowClearEmbed
                )
              }
              onClick={() => {
                if (!displayRect && allowClearEmbed) {
                  void onClearEmbed?.();
                  return;
                }
                void handleApply();
              }}
            >
              {applying ? <Loader2 className="animate-spin" size={14} /> : null}
              {displayRect ? '保存嵌入图' : '清除嵌入图'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
